import { useEffect, useState } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Check, ChevronDown, ExternalLink, Calculator as CalcIcon, Tag } from "lucide-react";
import { getLanding, LANDING_SLUGS } from "@/data/landingPages";
import { LogoLockup } from "@/components/Logo";
import Footer from "@/components/Footer";

const SITE = "https://prolancer.app";
const PROLANCER_SIGNUP = "https://prolancer.sa";

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, json) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
}

export default function LandingPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  const data = getLanding(slug);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (!data) return;
    // Reset scroll to the top so the user sees the new guide from its heading.
    try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch { window.scrollTo(0, 0); }
    const url = `${SITE}/${data.slug}`;
    document.title = data.metaTitle;
    document.documentElement.setAttribute("dir", "rtl");
    document.documentElement.setAttribute("lang", "ar");
    setMeta("description", data.metaDesc);
    setMeta("keywords", data.kw);
    setMeta("robots", "index, follow, max-image-preview:large");
    setLink("canonical", url);
    // Open Graph
    setMeta("og:type", "article", "property");
    setMeta("og:title", data.metaTitle, "property");
    setMeta("og:description", data.metaDesc, "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", `${SITE}/prolancer-bird.png`, "property");
    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", data.metaTitle);
    setMeta("twitter:description", data.metaDesc);

    // FAQ Schema
    setJsonLd("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    // Breadcrumb Schema
    setJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "برولانسر", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "حاسبة أسعار العمل الحر", item: SITE + "/" },
        { "@type": "ListItem", position: 3, name: data.h1, item: url },
      ],
    });

    // Article Schema
    setJsonLd("ld-article", {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: data.h1,
      description: data.metaDesc,
      inLanguage: "ar",
      mainEntityOfPage: url,
      author: { "@type": "Organization", name: "برولانسر", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "برولانسر",
        logo: { "@type": "ImageObject", url: `${SITE}/prolancer-bird.png` },
      },
    });

    return () => {
      // cleanup injected JSON-LD so main page doesn't inherit duplicates
      ["ld-faq", "ld-breadcrumb", "ld-article"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [data]);

  if (!slug) return <Navigate to="/" replace />;
  if (!data) return <Navigate to="/" replace />;

  const relatedSlugs = LANDING_SLUGS.filter((s) => s !== slug).slice(0, 4);

  return (
    <div className="dark min-h-screen bg-[#182138] text-white" dir="rtl">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#1f2a47]/85 border-b border-[#2e3a5c]" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" aria-label="برولانسر — الصفحة الرئيسية">
            <LogoLockup lang="ar" theme="dark" />
          </Link>
          <Link
            to="/"
            data-testid="landing-open-calculator"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#18c98f] text-white text-xs sm:text-sm font-extrabold hover:bg-[#18c98f] transition"
          >
            <CalcIcon className="h-4 w-4" />
            افتح الحاسبة
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#2e3a5c]">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            background:
              "radial-gradient(ellipse at top left, rgba(24,201,143,0.4), transparent 60%), radial-gradient(ellipse at bottom right, rgba(24,201,143,0.2), transparent 60%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-4 text-xs text-white/60 font-arabic">
            <Link to="/" className="hover:text-[#18c98f]">برولانسر</Link>
            <span className="mx-2">›</span>
            <Link to="/" className="hover:text-[#18c98f]">حاسبة أسعار العمل الحر</Link>
            <span className="mx-2">›</span>
            <span className="text-white/80">{data.h1}</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#18c98f]/15 border border-[#18c98f]/40 text-[#18c98f] text-[10px] font-bold uppercase tracking-[0.3em] mb-4">
            <Tag className="h-3 w-3" />
            {data.tagline}
          </div>
          <h1
            data-testid="landing-h1"
            className="font-arabic font-bold tracking-tight text-3xl sm:text-5xl lg:text-6xl max-w-4xl leading-tight"
          >
            {data.h1}
          </h1>
          <p className="mt-5 max-w-3xl text-base sm:text-lg text-white/70 font-arabic leading-relaxed">
            {data.metaDesc}
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/"
              data-testid="landing-cta-calc"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#18c98f] text-white text-sm font-extrabold font-arabic hover:bg-[#18c98f] transition shadow-[0_10px_30px_-10px_rgba(24,201,143,0.6)]"
            >
              <CalcIcon className="h-4 w-4" />
              احسب سعرك الآن مجانًا
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <a
              href={PROLANCER_SIGNUP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-cta-signup"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1f2a47] border border-[#2e3a5c] text-white text-sm font-bold font-arabic hover:border-[#18c98f] transition"
            >
              <Sparkles className="h-4 w-4 text-[#18c98f]" />
              ابدأ الآن مع برولانسر
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* Price table */}
      <section className="border-b border-[#2e3a5c]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <h2 className="text-2xl sm:text-3xl font-bold font-arabic mb-6">
            جدول الأسعار التقديرية
          </h2>
          <div className="rounded-2xl border border-[#2e3a5c] overflow-hidden" data-testid="landing-price-table">
            <table className="w-full text-sm sm:text-base">
              <thead className="bg-[#1f2a47]">
                <tr>
                  <th className="text-start px-5 py-3 font-arabic font-bold text-[#18c98f] text-xs sm:text-sm uppercase tracking-widest">
                    الفئة
                  </th>
                  <th className="text-end px-5 py-3 font-arabic font-bold text-[#18c98f] text-xs sm:text-sm uppercase tracking-widest">
                    متوسط السعر
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.priceTable.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-t border-[#2e3a5c] ${i % 2 === 0 ? "bg-[#1f2a47]/40" : ""}`}
                  >
                    <td className="px-5 py-4 font-arabic">{row.role}</td>
                    <td className="px-5 py-4 text-end font-mono-num font-bold text-[#18c98f]" dir="ltr">
                      {row.perHour}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-white/50 font-arabic">
            * الأسعار تقديرية حسب متوسط السوق السعودي 2026 — استخدم الحاسبة لتعديلها حسب خبرتك وتفاصيل مشروعك.
          </p>
        </div>
      </section>

      {/* Long content */}
      <section className="border-b border-[#2e3a5c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10">
          {data.sections.map((s, i) => (
            <article key={i} data-testid={`landing-section-${i}`}>
              <h2 className="text-xl sm:text-2xl font-bold font-arabic text-[#18c98f]">
                {s.h}
              </h2>
              <p className="mt-3 text-sm sm:text-base leading-loose text-white/85 font-arabic">
                {s.p}
              </p>
            </article>
          ))}

          {/* Mid-content CTA */}
          <div className="rounded-2xl border border-[#18c98f]/40 bg-[#18c98f]/10 p-6 text-center">
            <Sparkles className="h-6 w-6 text-[#18c98f] mx-auto" />
            <h3 className="mt-3 text-lg sm:text-xl font-bold font-arabic">
              جاهز لتسعير خدمتك خلال 30 ثانية؟
            </h3>
            <p className="mt-2 text-sm text-white/70 font-arabic">
              استخدم حاسبة أسعار العمل الحر، ثم انشر خدمتك مباشرة على برولانسر.
            </p>
            <div className="mt-5 flex flex-wrap gap-3 justify-center">
              <Link
                to="/"
                data-testid="landing-mid-cta-calc"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#18c98f] text-white text-sm font-extrabold font-arabic hover:bg-[#18c98f] transition"
              >
                <CalcIcon className="h-4 w-4" />
                افتح الحاسبة
              </Link>
              <a
                href={PROLANCER_SIGNUP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="landing-mid-cta-signup"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1f2a47] border border-[#2e3a5c] text-white text-sm font-bold font-arabic hover:border-[#18c98f] transition"
              >
                سجّل في برولانسر
                <ExternalLink className="h-3.5 w-3.5 text-[#18c98f]" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[#2e3a5c]" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="text-2xl sm:text-3xl font-bold font-arabic">
            الأسئلة الشائعة
          </h2>
          <p className="mt-2 text-sm text-white/60 font-arabic">
            أكثر الأسئلة التي يطرحها المستقلون السعوديون عند تسعير هذه الخدمة.
          </p>

          <div className="mt-8 space-y-3">
            {data.faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={i}
                  data-testid={`landing-faq-${i}`}
                  className={`rounded-2xl border transition-all ${
                    open
                      ? "bg-[#1f2a47] border-[#18c98f]/40"
                      : "bg-[#1f2a47] border-[#2e3a5c] hover:border-[#18c98f]/40"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    className="w-full flex items-start gap-3 text-start px-5 py-4"
                  >
                    <Check className="h-5 w-5 text-[#18c98f] shrink-0 mt-0.5" />
                    <span className="flex-1 text-base sm:text-lg font-bold font-arabic">
                      {f.q}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 text-[#18c98f] shrink-0 transition-transform duration-300 ${
                        open ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 pt-0 text-sm leading-relaxed text-white/80 font-arabic border-t border-[#2e3a5c]">
                      <p className="mt-3">{f.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="border-b border-[#2e3a5c]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="text-2xl sm:text-3xl font-bold font-arabic">
            صفحات أسعار أخرى قد تهمّك
          </h2>
          <p className="mt-2 text-sm text-white/60 font-arabic">
            اضغط على أي بطاقة لفتح دليل تسعير متكامل مع متوسطات الأسعار، نماذج التسعير، ونصائح لتفادي الأخطاء الشائعة.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {relatedSlugs.map((s) => {
              const rel = getLanding(s);
              if (!rel) return null;
              // Build a short useful preview: pick first price row + first section snippet.
              const firstPrice = Array.isArray(rel.priceTable) && rel.priceTable.length > 0 ? rel.priceTable[0] : null;
              const sampleSection = Array.isArray(rel.sections) && rel.sections.length > 0 ? rel.sections[0] : null;
              const excerpt = (rel.metaDesc || (sampleSection && sampleSection.p) || "").slice(0, 180);
              return (
                <Link
                  key={s}
                  to={`/${s}`}
                  data-testid={`landing-related-${s}`}
                  className="rounded-2xl border border-[#2e3a5c] bg-[#1f2a47] p-5 hover:border-[#18c98f] hover:bg-[#1f2a47] transition group flex flex-col"
                >
                  <div className="text-[10px] font-bold text-[#18c98f] uppercase tracking-widest">
                    {rel.tagline}
                  </div>
                  <div className="mt-2 text-base sm:text-lg font-bold font-arabic leading-snug group-hover:text-[#18c98f] transition">
                    {rel.h1}
                  </div>
                  <p className="mt-3 text-xs sm:text-sm leading-relaxed text-white/70 font-arabic">
                    {excerpt}{excerpt.length >= 180 ? "…" : ""}
                  </p>
                  {firstPrice ? (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#18c98f]/10 border border-[#18c98f]/30 px-3 py-1.5 text-[11px] font-bold text-[#18c98f] self-start font-arabic">
                      <span>{firstPrice.role}:</span>
                      <span dir="ltr">{firstPrice.perHour || firstPrice.price || firstPrice.range || ""}</span>
                    </div>
                  ) : null}
                  <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#18c98f] group-hover:gap-2.5 transition-all font-arabic">
                    اقرأ الدليل كاملًا
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-b border-[#2e3a5c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Sparkles className="h-8 w-8 text-[#18c98f] mx-auto" />
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold font-arabic">
            ابدأ الآن مع برولانسر
          </h2>
          <p className="mt-3 max-w-xl mx-auto text-sm sm:text-base text-white/70 font-arabic leading-relaxed">
            سجّل مجانًا، احسب سعرك خلال 30 ثانية، وانشر خدمتك على أكبر منصة عمل حر سعودية.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link
              to="/"
              data-testid="landing-final-cta-calc"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#18c98f] text-white text-sm font-extrabold font-arabic hover:bg-[#18c98f] transition shadow-[0_10px_30px_-10px_rgba(24,201,143,0.6)]"
            >
              <CalcIcon className="h-4 w-4" />
              افتح الحاسبة الآن
            </Link>
            <a
              href={PROLANCER_SIGNUP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-final-cta-signup"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1f2a47] border border-[#2e3a5c] text-white text-sm font-bold font-arabic hover:border-[#18c98f] transition"
            >
              سجّل في برولانسر
              <ExternalLink className="h-3.5 w-3.5 text-[#18c98f]" />
            </a>
          </div>
        </div>
      </section>

      <Footer lang="ar" theme="dark" />
    </div>
  );
}
